import { DestroyRef, Injectable, Signal, effect, inject, signal, untracked } from '@angular/core';
import { Route, Router } from '@angular/router';

import { createTool } from '@hashbrownai/angular';
import { fryHashbrown } from '@hashbrownai/core';
import type { Chat } from '@hashbrownai/core';
import { Client } from '@modelcontextprotocol/sdk/client/index.js';
import { StreamableHTTPClientTransport } from '@modelcontextprotocol/sdk/client/streamableHttp.js';
import { CallToolResultSchema } from '@modelcontextprotocol/sdk/types.js';

import { environment } from '@env/environment';
import { AppRouteData } from '../../app.routes';
import { AuthService } from '@core/services/auth.service';
import { LanguageService } from '@core/services/language.service';
import { AssistantContextService } from '@shared/services/assistant-context.service';
import { getAssistantAccessToken, getAssistantSpaceId } from '@core/services/assistant-auth-bridge';
import { AssistantHistoryService } from './assistant-history.service';
import { AssistantUiService } from './assistant-ui.service';
import { AssistantPlanStep } from './assistant.types';
import { buildAssistantSystemPrompt } from './assistant-prompt';

type AnyTool = Chat.AnyTool;
type AnyMessage = Chat.Message<string, AnyTool>;

/**
 * Текст «продолжай» отправляется от имени пользователя (другого входа у
 * hashbrown нет), поэтому помечаем его сентинелом — панель рисует такие
 * сообщения служебной плашкой, а не репликой пользователя.
 */
export const AUTO_CONTINUE_SENTINEL = '/continue';
/** Сколько раз подряд разрешаем себе подтолкнуть агента без участия юзера. */
const MAX_AUTO_CONTINUES = 4;

interface ChatRef {
  value: Signal<AnyMessage[]>;
  isReceiving: Signal<boolean>;
  isRunningToolCalls: Signal<boolean>;
  sendMessage: (message: Chat.UserMessage) => void;
  stop: (clearStreamingMessage?: boolean) => void;
  setMessages: (messages: AnyMessage[]) => void;
  destroy: () => void;
}

/**
 * Чат-рантайм встроенного агента.
 *
 * Живёт в корне приложения, а не в компоненте панели: переписка и запущенные
 * тулы обязаны переживать навигацию — агент половину работы как раз и делает
 * тем, что уводит пользователя на другую страницу.
 *
 * Разделение ответственности: здесь — модель, тулы и история; всё, что
 * трогает страницу, уходит в `AssistantUiService`. Запись в бэк отсюда
 * невозможна by design: MCP-тулы фильтруются по readOnlyHint.
 */
@Injectable({ providedIn: 'root' })
export class AssistantService {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly ui = inject(AssistantUiService);
  private readonly context = inject(AssistantContextService);
  private readonly language = inject(LanguageService);
  private readonly history = inject(AssistantHistoryService);
  private readonly destroyRef = inject(DestroyRef);

  readonly ready = signal(false);
  readonly error = signal<string | null>(null);
  readonly plan = signal<readonly AssistantPlanStep[]>([]);
  /** Агент сам себя подтолкнул — показываем это честно. */
  readonly autoContinuing = signal(false);

  private readonly chatRef = signal<ChatRef | null>(null);
  readonly chat = this.chatRef.asReadonly();

  readonly messages = signal<AnyMessage[]>([]);
  readonly busy = signal(false);

  private mcpClient: Client | null = null;
  private tools: AnyTool[] = [];
  /** Имена read-only MCP-тулов — по ним воскрешаем оборванные вызовы. */
  private readonly mcpToolNames = new Set<string>();

  private autoContinues = 0;
  private wasBusy = false;

  constructor() {
    void this.initialize();

    // Режим («спрашивать» / «делать автоматом») зашит в системный промпт —
    // при переключении пересобираем инстанс, перенося историю как есть.
    effect(() => {
      const mode = this.ui.mode();
      untracked(() => {
        const current = this.chatRef();
        if (!current) return;
        const messages = current.value();
        current.destroy();
        this.chatRef.set(this.createChat(this.tools, messages, mode));
      });
    });

    // Зеркалим сообщения/занятость наружу и пишем историю в localStorage,
    // чтобы перезагрузка страницы не стирала разговор.
    effect(() => {
      const c = this.chatRef();
      if (!c) return;
      const messages = c.value();
      const busy = c.isReceiving() || c.isRunningToolCalls();
      untracked(() => {
        this.messages.set(messages);
        this.busy.set(busy);
        this.history.save(messages);
        this.maybeAutoContinue(busy, messages);
        this.maybeNameConversation(busy, messages);
      });
    });

    this.destroyRef.onDestroy(() => {
      this.ui.rejectAll();
      this.chatRef()?.destroy();
      void this.mcpClient?.close();
    });
  }

  // ------------------------------------------------------------------ public

  send(text: string): void {
    const trimmed = text.trim();
    const c = this.chatRef();
    if (!trimmed || !c || this.busy()) return;
    this.autoContinues = 0;
    c.sendMessage({ role: 'user', content: trimmed });
  }

  stop(): void {
    this.chatRef()?.stop(true);
    this.autoContinues = MAX_AUTO_CONTINUES; // пользователь нажал стоп — не подталкиваем
  }

  /** Очистить текущую переписку, оставив её в истории пустой. */
  clear(): void {
    this.chatRef()?.setMessages([]);
    this.ui.clearProposals();
    this.plan.set([]);
    this.autoContinues = 0;
    this.history.remove(this.history.activeId());
    this.startNew();
  }

  /** Забыть все переписки разом — «очистить историю» в панели. */
  forgetAll(): void {
    this.history.clearAll();
    this.swapMessages([]);
  }

  /** Новый разговор: прежний уезжает в историю как есть. */
  startNew(): void {
    this.history.startNew();
    this.swapMessages([]);
  }

  /** Открыть переписку из истории — вместе со всем, что в ней было. */
  openConversation(id: string): void {
    if (id === this.history.activeId()) return;
    this.swapMessages(this.history.switchTo(id));
  }

  /** Убрать переписку из истории. Активную заменяет пустая. */
  removeConversation(id: string): void {
    const wasActive = id === this.history.activeId();
    this.history.remove(id);
    if (wasActive) this.swapMessages([]);
  }

  /**
   * Пересадить рантайм на другой набор сообщений.
   *
   * Не `setMessages`: тот оставил бы висеть предложения и план от прошлого
   * разговора — человек переключил тему, а на экране осталась карточка
   * «применить» из предыдущей.
   */
  private swapMessages(messages: AnyMessage[]): void {
    this.ui.rejectAll();
    this.ui.clearProposals();
    this.plan.set([]);
    this.autoContinues = 0;
    const current = this.chatRef();
    current?.destroy();
    this.chatRef.set(this.createChat(this.tools, messages, this.ui.mode()));
  }

  /** Служебное ли это сообщение-подталкивание (панель рисует его иначе). */
  isAutoContinue(message: AnyMessage): boolean {
    return message.role === 'user' && message.content === AUTO_CONTINUE_SENTINEL;
  }

  // -------------------------------------------------------------- lifecycle

  private async initialize(): Promise<void> {
    try {
      const mcpTools = await this.connectMcp();
      this.tools = [...mcpTools, ...this.buildUiTools()];
      this.chatRef.set(this.createChat(this.tools, this.history.activeMessages(), this.ui.mode()));
      this.ready.set(true);
      this.settleOrphanToolCalls();
    } catch (e: unknown) {
      this.error.set(e instanceof Error ? e.message : String(e));
    }
  }

  private createChat(tools: AnyTool[], messages: AnyMessage[], mode: ReturnType<AssistantUiService['mode']>): ChatRef {
    const hashbrown = fryHashbrown({
      apiUrl: `${environment.hashbrownProxyUrl}/api/chat`,
      middleware: [init => this.withAuth(init)],
      model: environment.assistantModel,
      system: buildAssistantSystemPrompt({
        appName: environment.appName,
        mode,
        uiLanguage: this.language.currentLang(),
      }),
      messages,
      tools,
      debugName: 'speaknode-assistant',
    });

    const teardown = hashbrown.sizzle();
    const unsubs: Array<() => void> = [];

    // StateSignal → Angular signal: читаем текущее значение и подписываемся.
    const toNg = <T>(source: { (): T; subscribe(cb: (v: T) => void): () => void }) => {
      const s = signal(source());
      unsubs.push(source.subscribe(v => s.set(v)));
      return s.asReadonly();
    };

    return {
      value: toNg(hashbrown.messages),
      isReceiving: toNg(hashbrown.isReceiving),
      isRunningToolCalls: toNg(hashbrown.isRunningToolCalls),
      sendMessage: hashbrown.sendMessage,
      stop: hashbrown.stop,
      setMessages: hashbrown.setMessages,
      destroy: () => {
        for (const un of unsubs) un();
        teardown();
      },
    };
  }

  /**
   * Имя разговору придумывает модель — она одна знает, о чём он был.
   *
   * Спрашиваем один раз, когда первый обмен закончился: раньше нечего
   * называть, позже — лишний запрос на каждую реплику. Отдельный
   * инстанс без тулов: этому вопросу не нужны ни MCP, ни страница, и
   * ответ не должен попасть в саму переписку.
   */
  private naming = false;

  private maybeNameConversation(busy: boolean, messages: AnyMessage[]): void {
    if (busy || this.naming) return;
    const id = this.history.activeId();
    const said = messages.filter(m => m.role === 'user' || m.role === 'assistant');
    if (!id || !this.history.needsTitle(id)) return;
    if (said.length < 2) return;

    this.naming = true;
    void this.askForTitle(said)
      .then(title => {
        if (title) {
          this.history.setTitle(id, title);
          this.history.markTitled(id);
        }
      })
      .finally(() => {
        this.naming = false;
      });
  }

  private askForTitle(messages: AnyMessage[]): Promise<string> {
    const transcript = messages
      .slice(0, 6)
      .map(m => `${m.role}: ${String((m as { content?: unknown }).content ?? '').slice(0, 400)}`)
      .join('\n');

    return new Promise<string>(resolve => {
      let done = false;
      const finish = (value: string) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        unsubscribe();
        teardown();
        resolve(value);
      };

      const hashbrown = fryHashbrown({
        apiUrl: `${environment.hashbrownProxyUrl}/api/chat`,
        middleware: [init => this.withAuth(init)],
        model: environment.assistantModel,
        system:
          'You name conversations. Answer with a short title — three to six words, no quotes, no ' +
          'trailing period — in the language the user speaks. Describe what the user wanted done, ' +
          'not what the assistant replied.',
        messages: [] as AnyMessage[],
        tools: [],
        debugName: 'speaknode-assistant-title',
      });

      const teardown = hashbrown.sizzle();
      const unsubscribe = hashbrown.messages.subscribe(list => {
        const last = list[list.length - 1];
        if (!last || last.role !== 'assistant') return;
        const text = String((last as { content?: unknown }).content ?? '').trim();
        if (text && !hashbrown.isReceiving()) finish(text.split('\n')[0]);
      });

      // Имя — украшение: не дождались, останется черновое из первой реплики.
      const timer = setTimeout(() => finish(''), 15000);
      hashbrown.sendMessage({ role: 'user', content: transcript });
    });
  }

  /** Токен и спейс в заголовки — одинаково для чата и для имени разговора. */
  private withAuth(init: RequestInit): RequestInit {
    const token = getAssistantAccessToken();
    const spaceId = getAssistantSpaceId();
    const extra: Record<string, string> = {};
    if (token) extra['Authorization'] = `Bearer ${token}`;
    if (spaceId) extra['x-space-id'] = spaceId;
    if (Object.keys(extra).length === 0) return init;
    return { ...init, headers: { ...(init.headers as Record<string, string> | undefined), ...extra } };
  }

  /**
   * Перезагрузка страницы обрывает tool-call'ы на полуслове: в истории
   * остаётся `status: 'pending'` без результата, и модель на следующем
   * запросе упирается в незакрытый вызов. Read-only MCP-вызов просто
   * повторяем (он безопасен), UI-вызов закрываем отказом — состояние
   * страницы за это время могло уехать.
   */
  private settleOrphanToolCalls(): void {
    const c = this.chatRef();
    if (!c) return;
    const messages = c.value();
    const orphans: Array<{ name: string; args: unknown; toolCallId: string }> = [];
    for (const msg of messages as Array<AnyMessage & { toolCalls?: Array<Record<string, any>> }>) {
      if (msg.role !== 'assistant' || !Array.isArray(msg.toolCalls)) continue;
      for (const call of msg.toolCalls) {
        if (call['status'] === 'pending') {
          orphans.push({ name: call['name'], args: call['args'], toolCallId: call['toolCallId'] });
        }
      }
    }
    if (!orphans.length) return;

    void Promise.all(
      orphans.map(async call => {
        if (this.mcpToolNames.has(call.name) && this.mcpClient) {
          try {
            const value = await this.callMcpTool(call.name, call.args as Record<string, unknown>);
            return { toolCallId: call.toolCallId, result: { status: 'fulfilled', value } as const };
          } catch (e: unknown) {
            return { toolCallId: call.toolCallId, result: { status: 'rejected', reason: e } as const };
          }
        }
        return {
          toolCallId: call.toolCallId,
          result: {
            status: 'rejected',
            reason: 'Interrupted by a page reload. Re-check the current page state before retrying.',
          } as const,
        };
      }),
    ).then(settled => {
      const byId = new Map(settled.map(s => [s.toolCallId, s.result]));
      const current = this.chatRef();
      if (!current) return;
      const updated = current.value().map(m => {
        const msg = m as AnyMessage & { toolCalls?: Array<Record<string, any>> };
        if (msg.role !== 'assistant' || !Array.isArray(msg.toolCalls)) return m;
        return {
          ...msg,
          toolCalls: msg.toolCalls.map(call =>
            call['status'] === 'pending' && byId.has(call['toolCallId'])
              ? { ...call, status: 'done', result: byId.get(call['toolCallId']) }
              : call,
          ),
        } as AnyMessage;
      });
      current.setMessages(updated);
    });
  }

  // ------------------------------------------------------------ auto-continue

  /**
   * Лечим главную боль: агент отчитывается о первом шаге и замолкает, а
   * пользователь дописывает «продолжай». Пока в плане есть незакрытые шаги,
   * ничего не ждёт подтверждения и мы не упёрлись в лимит — подталкиваем сами.
   */
  private maybeAutoContinue(busy: boolean, messages: AnyMessage[]): void {
    const wasBusy = this.wasBusy;
    this.wasBusy = busy;
    if (busy || !wasBusy) return;

    this.autoContinuing.set(false);

    const unfinished = this.plan().some(s => s.status === 'pending' || s.status === 'in_progress');
    if (!unfinished) return;
    if (this.ui.hasPending()) return;
    if (this.autoContinues >= MAX_AUTO_CONTINUES) return;

    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant') return;
    // Агент задал вопрос и ждёт ответа — это не «затуп», это диалог.
    if (typeof last.content === 'string' && last.content.trimEnd().endsWith('?')) return;

    this.autoContinues += 1;
    this.autoContinuing.set(true);
    setTimeout(() => {
      const c = this.chatRef();
      if (!c || this.busy()) return;
      c.sendMessage({ role: 'user', content: AUTO_CONTINUE_SENTINEL });
    });
  }

  // ------------------------------------------------------------------- tools

  private buildUiTools(): AnyTool[] {
    return [
      createTool({
        name: 'getCurrentPageContext',
        description:
          'What the user is looking at right now: the current URL and, when the page publishes it, ' +
          'the entity type/id/title it is showing. Call this whenever the user says "this", "current" or "here".',
        handler: async () => ({
          url: this.router.url,
          context: this.context.pageContext() ?? { description: 'unknown page' },
          mode: this.ui.mode(),
        }),
      }) as AnyTool,

      createTool({
        name: 'getAppRoutes',
        description:
          'Pages you may navigate to: a list of { path, description }. Paths containing :param segments ' +
          'need a real id — resolve it from getCurrentPageContext or an MCP list tool first. ' +
          'Call this before navigateTo; never invent a route.',
        handler: async () => ({ routes: this.collectNavRoutes() }),
      }) as AnyTool,

      createTool({
        name: 'navigateTo',
        description:
          'Open an in-app page, exactly as if the user clicked the link — they see the app move. ' +
          'Returns the editable fields and the actions of the page you land on, so you can act straight away.',
        schema: {
          type: 'object',
          additionalProperties: false,
          required: ['path'],
          properties: {
            path: { type: 'string', description: 'Absolute path, e.g. "/agents" or "/agents/abc-123".' },
          },
        } as object,
        handler: async (input: unknown) => {
          const path = (input as { path?: unknown })?.path;
          if (typeof path !== 'string' || !path.startsWith('/')) {
            return {
              ok: false,
              error: 'path is required: an absolute in-app path such as "/agents/<id>". Pick one below.',
              routes: this.collectNavRoutes(),
            };
          }
          try {
            const target = this.router.parseUrl(path).toString();
            // Уже здесь — это успех, а не промах. Роутер на переход к
            // текущему адресу отвечает false (навигация пропущена), и мы
            // честно рапортовали «нет такого роута» про страницу, на
            // которой пользователь в этот момент стоял.
            const alreadyHere = sameUrl(this.router.url, target);
            const moved = alreadyHere ? true : await this.router.navigateByUrl(path);

            // Переход мог отменить гард: адрес не поменялся или увёл в
            // другое место. Это другая беда, чем «путь не существует», и
            // модели полезно знать, какая именно.
            if (!moved && !sameUrl(this.router.url, target)) {
              return {
                ok: false,
                error:
                  `The app stayed on "${this.router.url}" instead of opening "${path}". ` +
                  'Either the path is wrong — pick one of the routes below and substitute real ids — ' +
                  'or a guard refused (no access, the entity does not exist).',
                routes: this.collectNavRoutes(),
              };
            }

            await this.waitForPageReady();
            return {
              ok: true,
              url: this.router.url,
              ...(alreadyHere ? { note: 'The app was already on this page — nothing moved.' } : {}),
              fields: this.ui.listFields(),
              actions: this.ui.listActions(),
            };
          } catch (e: unknown) {
            const message = e instanceof Error ? e.message : String(e);
            // Роутер на несуществующий путь бросает NG04002 — превращаем
            // его в подсказку с каталогом, иначе модель гадает второй раз
            // тем же способом.
            if (message.includes('Cannot match any routes')) {
              return {
                ok: false,
                error: `No route matched "${path}". Pick one of the routes below and substitute real ids.`,
                routes: this.collectNavRoutes(),
              };
            }
            return { ok: false, error: message };
          }
        },
      }) as AnyTool,

      createTool({
        name: 'getPageFields',
        description:
          'Editable fields of the current page with their current values: { id, label, description, kind, options, value }. ' +
          'These ids are the only thing proposeFieldChange accepts, and they are valid only on this page. ' +
          'Long values come back shortened (valueTruncated: true) — read the whole thing with getFieldValue ' +
          'before rewriting it, never rewrite from the preview. `invalidFields` lists what the form currently ' +
          'rejects: fix those before running the save action, or the save will fail.',
        handler: async () => ({
          url: this.router.url,
          fields: this.ui.listFields(),
          invalidFields: this.ui.validationSummary(),
        }),
      }) as AnyTool,

      createTool({
        name: 'getFieldValue',
        description:
          'The full, untruncated value of one field of the current page. Use it for long values — a system ' +
          'prompt, a JSON schema — before proposing a rewrite.',
        schema: {
          type: 'object',
          additionalProperties: false,
          required: ['fieldId'],
          properties: {
            fieldId: { type: 'string', description: 'Field id from getPageFields.' },
          },
        } as object,
        handler: async (input: unknown) => {
          const fieldId = (input as { fieldId?: unknown })?.fieldId;
          if (typeof fieldId !== 'string') {
            return {
              ok: false,
              error: 'fieldId is required, as a string.',
              fields: this.ui.listFields().map(f => ({ id: f.id, label: f.label })),
            };
          }
          return this.ui.readField(fieldId);
        },
      }) as AnyTool,

      createTool({
        name: 'proposeFieldChange',
        description:
          'Change one field of the current page. The field is highlighted and scrolled into view, and — ' +
          'in "confirm" mode — waits for the user to approve it. Values are always strings: "true"/"false" ' +
          'for booleans, plain digits for numbers, the exact option value for enums, a JSON string for json ' +
          'fields. Change fields one at a time; nothing is persisted until you run the page\'s save action.',
        schema: {
          type: 'object',
          additionalProperties: false,
          required: ['fieldId', 'value', 'reason'],
          properties: {
            fieldId: { type: 'string', description: 'Field id from getPageFields.' },
            value: { type: 'string', description: 'The new value, as a string.' },
            reason: { type: 'string', description: 'One short sentence telling the user why. May be empty.' },
          },
        } as object,
        handler: async (input: unknown) => {
          const { fieldId, value, reason } = (input ?? {}) as Record<string, unknown>;
          if (typeof fieldId !== 'string' || typeof value !== 'string') {
            // Модель иногда присылает пустой объект аргументов. Сухое
            // «fieldId must be a string» не говорит ей, чем его заполнить,
            // и следующий вызов выходит таким же пустым — отдаём поля.
            return {
              ok: false,
              error: 'Both fieldId and value are required, as strings. Pick a fieldId from the list below.',
              fields: this.ui.listFields().map(f => ({ id: f.id, label: f.label, kind: f.kind })),
            };
          }
          return this.ui.proposeFieldChange(fieldId, value, typeof reason === 'string' ? reason : undefined);
        },
      }) as AnyTool,

      createTool({
        name: 'getPageActions',
        description:
          'Buttons the current page exposes to you: { id, label, description, dangerous }. ' +
          'Saving edits is one of these — field changes stay unsaved until you run it.',
        handler: async () => ({ url: this.router.url, actions: this.ui.listActions() }),
      }) as AnyTool,

      createTool({
        name: 'runPageAction',
        description:
          'Press one of the current page\'s buttons. Dangerous actions are always confirmed by the user, ' +
          'whatever the mode. Returns what the page reported back, plus the fields and actions the page ' +
          'exposes afterwards — a button often opens a panel with its own fields and its own button, and ' +
          'the work is not finished until you run that one too.',
        schema: {
          type: 'object',
          additionalProperties: false,
          required: ['actionId', 'reason'],
          properties: {
            actionId: { type: 'string', description: 'Action id from getPageActions.' },
            reason: { type: 'string', description: 'One short sentence telling the user why. May be empty.' },
          },
        } as object,
        handler: async (input: unknown) => {
          const { actionId, reason } = (input ?? {}) as Record<string, unknown>;
          if (typeof actionId !== 'string') {
            return {
              ok: false,
              error: 'actionId is required, as a string. Pick one of the actions below.',
              actions: this.ui.listActions(),
            };
          }
          const result = await this.ui.proposeAction(actionId, typeof reason === 'string' ? reason : undefined);

          // Кнопка часто не завершает дело, а открывает следующий шаг:
          // «Начать тест» сохраняет тест и открывает ящик запуска, где
          // ещё надо выбрать агента и нажать его кнопку. Модель видела
          // только «Тест обновлён» и считала, что прогон пошёл. Отдаём
          // поверхность страницы после нажатия — тогда следующий шаг
          // виден сам (SKN-473).
          await this.afterRender();
          return {
            ...result,
            url: this.router.url,
            fields: this.ui.listFields(),
            actions: this.ui.listActions(),
          };
        },
      }) as AnyTool,

      createTool({
        name: 'updatePlan',
        description:
          'Publish or update your checklist for the current request — the user sees it above the chat input. ' +
          'Send the whole list every time. Mark a step in_progress when you start it and done when it lands; ' +
          'while the list has unfinished steps you are expected to keep working without being asked to continue.',
        schema: {
          type: 'object',
          additionalProperties: false,
          required: ['steps'],
          properties: {
            steps: {
              type: 'array',
              description: 'The full checklist, in order.',
              items: {
                type: 'object',
                additionalProperties: false,
                required: ['title', 'status'],
                properties: {
                  title: { type: 'string', description: 'Short imperative step, e.g. "Read the last 15 sessions".' },
                  status: { type: 'string', enum: ['pending', 'in_progress', 'done', 'skipped'] },
                },
              },
            },
          },
        } as object,
        handler: async (input: unknown) => {
          const steps = (input as { steps?: unknown })?.steps;
          if (!Array.isArray(steps)) {
            // Пустой объект аргументов — обычное дело для модели. Показываем
            // ей форму вызова и текущий план, чтобы следующий вызов был целым.
            return {
              ok: false,
              error:
                'steps is required: the full checklist as an array, e.g. ' +
                '{"steps":[{"title":"Read the last 15 sessions","status":"in_progress"},' +
                '{"title":"Rewrite the prompt","status":"pending"}]}. Send every step every time.',
              currentPlan: this.plan(),
            };
          }
          const normalized: AssistantPlanStep[] = steps
            .filter((s): s is Record<string, unknown> => !!s && typeof s === 'object')
            .map(s => ({
              title: String(s['title'] ?? ''),
              status: (['pending', 'in_progress', 'done', 'skipped'] as const).includes(s['status'] as never)
                ? (s['status'] as AssistantPlanStep['status'])
                : 'pending',
            }))
            .filter(s => s.title.length > 0);
          this.plan.set(normalized);
          return { ok: true, steps: normalized.length };
        },
      }) as AnyTool,
    ];
  }

  /**
   * Роуты берём из `Router.config` — единственный источник правды. Роут
   * попадает в выдачу, только если у него есть `data.assistantNav.description`.
   */
  private collectNavRoutes(): Array<{ path: string; description: string }> {
    const out: Array<{ path: string; description: string }> = [];
    const walk = (routes: Route[], prefix: string) => {
      for (const r of routes) {
        const seg = r.path ?? '';
        const full = seg ? `${prefix}/${seg}` : prefix;
        const nav = (r.data as AppRouteData | undefined)?.assistantNav;
        if (nav) out.push({ path: full || '/', description: nav.description });
        if (r.children) walk(r.children, full);
      }
    };
    walk(this.router.config, '');
    return out;
  }

  /**
   * Один проход отрисовки. Поля и действия регистрируются директивами при
   * отрисовке, так что сразу после нажатия кнопки открывшаяся панель в
   * реестре ещё не числится.
   */
  private afterRender(): Promise<void> {
    return new Promise<void>(resolve => setTimeout(resolve, 50));
  }

  /**
   * Ждём, пока приехавшая страница зарегистрирует свои поля: компоненты
   * делают это после загрузки данных, а не в момент NavigationEnd.
   */
  private async waitForPageReady(timeoutMs = 4000): Promise<void> {
    // navigateByUrl резолвится уже после NavigationEnd, так что ждать
    // события смысла нет — ждём, пока компонент отрисуется и зарегистрирует
    // свою поверхность (поля приезжают после загрузки данных страницы).
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      if (this.ui.listFields().length > 0 || this.ui.listActions().length > 0) return;
      await new Promise<void>(resolve => setTimeout(resolve, 120));
    }
  }

  /**
   * MCP — только чтение. Тулы без `readOnlyHint` (то есть всё, что не GET)
   * до модели не доезжают вовсе: любое изменение обязано идти через
   * интерфейс, чтобы пользователь его видел.
   */
  /**
   * Поднимает сессию MCP.
   *
   * Заголовки замораживаются на момент connect, поэтому свежий JWT и
   * x-space-id (смена воркспейса) прокидываем через собственный fetch.
   */
  private async openMcpSession(): Promise<void> {
    const url = new URL(`${environment.generatedApiUrl}/mcp`);

    const fetchWithFreshHeaders: typeof fetch = (input, init) => {
      const token = this.auth.getAccessToken();
      const spaceId = getAssistantSpaceId();
      const headers = new Headers(init?.headers);
      if (token) headers.set('Authorization', `Bearer ${token}`);
      if (spaceId) headers.set('x-space-id', spaceId);
      return fetch(input, { ...init, headers });
    };

    const client = new Client({ name: 'speaknode-frontend', version: '2.0.0' });
    await client.connect(new StreamableHTTPClientTransport(url, { fetch: fetchWithFreshHeaders }));
    this.mcpClient = client;
  }

  /**
   * Сессия MCP живёт на бэкенде в памяти: перезапуск сервиса, деплой или
   * простой — и её больше нет. Клиент об этом не узнаёт и продолжает
   * ходить со старым идентификатором, получая 404 «Session not found».
   * Для модели это выглядит как сломанный инструмент, и она либо врёт
   * пользователю, либо долбится в него до конца ходов.
   *
   * Поэтому сессию переподнимаем на месте. Параллельные вызовы ждут одно
   * и то же переподключение: у агента в работе бывает несколько тулов
   * разом, и каждый не должен заводить свою сессию.
   */
  private reconnecting: Promise<void> | null = null;

  private reconnectMcp(): Promise<void> {
    this.reconnecting ??= (async () => {
      try {
        await this.mcpClient?.close();
      } catch {
        // старую сессию всё равно закрывать некому
      }
      await this.openMcpSession();
    })().finally(() => {
      this.reconnecting = null;
    });
    return this.reconnecting;
  }

  /**
   * Вызов MCP-тула с одной попыткой переподключения.
   *
   * `request`, а не `callTool`: callTool сверяет structuredContent с
   * output-схемой тула и на расхождении выбрасывает весь ответ. Схема —
   * описание для других клиентов; наш потребитель — модель, читающая
   * текст. Из-за одной неточности в схеме она получала «вызов не удался»
   * вместо данных, не могла отличить сломанную схему от сломанного
   * запроса и повторяла вызов до конца ходов. Сами схемы чиним на бэке,
   * но зависеть от их безупречности здесь нельзя.
   */
  private async callMcpTool(name: string, args: Record<string, unknown>): Promise<unknown> {
    const send = () =>
      this.mcpClient!.request({ method: 'tools/call', params: { name, arguments: args } }, CallToolResultSchema);

    try {
      return await send();
    } catch (e: unknown) {
      if (!isLostSessionError(e)) throw e;
      await this.reconnectMcp();
      return await send();
    }
  }

  private async connectMcp(): Promise<AnyTool[]> {
    await this.openMcpSession();

    const { tools: mcpTools } = await this.mcpClient!.listTools();
    const readOnly = mcpTools.filter(t => t.annotations?.readOnlyHint === true);
    const skipped = mcpTools.length - readOnly.length;
    console.info(
      `[assistant] MCP: ${readOnly.length} read-only tools exposed, ${skipped} writing tools withheld ` +
        '(writes go through the UI).',
    );

    return readOnly.map(t => {
      this.mcpToolNames.add(t.name);
      return createTool({
        name: t.name,
        description: t.description ?? '',
        schema: mcpToolSchema(t.inputSchema as Record<string, unknown> | undefined),
        handler: async (input: unknown) => {
          try {
            return await this.callMcpTool(t.name, input as Record<string, unknown>);
          } catch (e: unknown) {
            // Error.message не перечисляемое, и JSON.stringify его теряет:
            // в историю уезжало `{"code":-32602,"name":"McpError"}`, по
            // которому ни модель, ни человек не поймут, что случилось.
            // Разворачиваем в обычный объект.
            const err = e as { message?: string; code?: number; data?: unknown };
            return {
              error: err?.message ?? String(e),
              code: err?.code,
              data: err?.data,
              tool: t.name,
            };
          }
        },
      }) as AnyTool;
    });
  }
}

/**
 * Схема аргументов MCP-тула в том виде, в каком её получает модель.
 *
 * `required` — ровно то, что объявил сервер. Раньше при отсутствии списка
 * здесь подставлялись ВСЕ свойства, и каждый необязательный фильтр
 * становился обязательным: модель была обязана что-то прислать, присылала
 * пустую строку или null, а ручка отвечала «An error occurred invoking …»
 * без поля и без причины. Так не работали `tests_get_test_runs`,
 * `sessions_get_agent_sessions` и `models_get_models_hierarchy` (SKN-473).
 *
 * Strict-режим OpenAI собирает llm-proxy: он сам добирает `required` до
 * всех свойств и разрешает в них null — но только там, где сервер
 * значения не потребовал. Поэтому подменять `required` здесь нельзя:
 * необязательное перестаёт быть необязательным, и null становится
 * невыразим.
 */
export function mcpToolSchema(inputSchema: Record<string, unknown> | undefined): object {
  const schema = inputSchema ?? { type: 'object', properties: {} };
  return {
    ...schema,
    additionalProperties: false,
    required: (schema['required'] as string[] | undefined) ?? [],
  };
}

/**
 * Один ли это адрес. Хвостовой слэш и пустой фрагмент значения не имеют:
 * `/agents/42` и `/agents/42/` — одна и та же страница.
 */
function sameUrl(a: string, b: string): boolean {
  const norm = (url: string) => url.replace(/#$/, '').replace(/\/+$/, '') || '/';
  return norm(a) === norm(b);
}

/**
 * Потеряна ли сессия MCP.
 *
 * Отличать важно: на потере сессии надо переподключиться и повторить, а
 * на ошибке схемы (-32602) повтор ничего не изменит — будет тот же ответ
 * и потраченный ход. Сервер сообщает о потере кодом -32001 «Session not
 * found» и 404-м статусом, и приходит это внутри текста транспортной
 * ошибки, а не отдельным полем.
 */
export function isLostSessionError(e: unknown): boolean {
  const err = e as { message?: string; code?: number } | null;
  const text = String(err?.message ?? e ?? '');
  return text.includes('Session not found') || text.includes('-32001') || err?.code === 404;
}
